/**
 * Thin fetch wrapper for the Nest backend.
 *
 * - Reads JWT from localStorage and attaches Authorization header
 * - Unwraps the ResponseInterceptor envelope `{ code, data, message }`
 * - Throws ApiError with code + message on non-2xx so TanStack Query can
 *   show error UI uniformly
 */

import { Routes } from '@tg-leads/contracts';

const TOKEN_KEY = 'tg-leads.jwt';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export async function apiFetch<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
  };
  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;

  const res = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const envelope = text
    ? (JSON.parse(text) as { code?: number; data?: T; message?: string })
    : {};

  if (!res.ok) {
    // 401 / 403 → kick to login (token rejected)
    if (res.status === 401) {
      setToken(null);
      if (!path.startsWith(Routes.auth.login)) {
        window.location.assign('/login');
      }
    }
    throw new ApiError(
      res.status,
      typeof envelope.message === 'string' ? envelope.message : 'request_failed',
      envelope.message ?? `HTTP ${res.status}`,
    );
  }

  return (envelope.data as T) ?? (undefined as T);
}

export const api = {
  get: <T>(path: string) => apiFetch<T>('GET', path),
  post: <T>(path: string, body?: unknown) => apiFetch<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => apiFetch<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>('PATCH', path, body),
  delete: <T = void>(path: string, body?: unknown) =>
    apiFetch<T>('DELETE', path, body),
};
